import type {
  Breadcrumb,
  Primitive,
  User,
  ParameterizedString,
  Log,
} from "@sentry/core";
import type * as Sentry from "@sentry/vue";
import { defineNuxtPlugin } from "nuxt/app";

type SentryProvides = {
  sentrySetContext: typeof Sentry.setContext;
  sentrySetUser: typeof Sentry.setUser;
  sentrySetTag: typeof Sentry.setTag;
  sentryAddBreadcrumb: typeof Sentry.addBreadcrumb;
  sentryLogger: typeof Sentry.logger;
  sentryCaptureException: typeof Sentry.captureException;
};

export default defineNuxtPlugin<SentryProvides>({
  name: "ckweb:sentry",
  dependsOn: ["ckweb:settings", "ckweb:common"],
  setup: async (nuxtApp) => {
    const publicConfig = nuxtApp.$config.public as unknown as {
      PROD: boolean;
      SENTRY_ENABLED: string;
      API_URL: string;
      SENTRY_DSN: string;
      SENTRY_RELEASE: string;
      SENTRY_ENVIRONMENT: string;
      SENTRY_TRACE_PROPAGATION_TARGET: string;
    };

    let provide: SentryProvides = {
      sentrySetContext: (
        _name: string,
        _context: {
          [key: string]: unknown;
        } | null,
      ) => {},

      sentrySetUser: (_user: User | null) => {},

      sentrySetTag: (_key: string, _value: Primitive) => {},

      sentryAddBreadcrumb: (_breadcrumb: Breadcrumb) => {},

      sentryLogger: {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        debug(message: ParameterizedString, attributes?: Log["attributes"]) {},
        trace(message: ParameterizedString, attributes?: Log["attributes"]) {},
        warn(message: ParameterizedString, attributes?: Log["attributes"]) {},
        fatal(message: ParameterizedString, attributes?: Log["attributes"]) {},
        info(message: ParameterizedString, attributes?: Log["attributes"]) {},
        error(message: ParameterizedString, attributes?: Log["attributes"]) {},
        /* eslint-enable @typescript-eslint/no-unused-vars */
        fmt() {
          return "";
        },
      },

      sentryCaptureException: (
        _exception: unknown,
        _captureContext?: Sentry.ExclusiveEventHintOrCaptureContext,
      ) => "",
    };
    if (
      (import.meta.env.VITE_DEV_MODE_SENTRY ||
        $ck.utils.parseBoolean(publicConfig.PROD)) &&
      $ck.utils.parseBoolean(publicConfig?.SENTRY_ENABLED)
    ) {
      const {
        replayIntegration,
        browserTracingIntegration,
        feedbackIntegration,
        httpClientIntegration,
        reportingObserverIntegration,
        init,
        showReportDialog,
        setContext,
        setUser,
        setTag,
        addBreadcrumb,
        captureException,
        vueIntegration,
        logger,
      } = await import("@sentry/vue");
      console.info("Sentry Enabled...");
      let API_URL = publicConfig.API_URL;
      if (import.meta.client && publicConfig.API_URL === "/") {
        API_URL = window.location.href;
      }
      const apiUrlObj = new URL(API_URL);
      const networkDetailAllowUrls = [
        new RegExp(`${apiUrlObj.origin}/api/.*`),
        window.location.origin,
        "https://sentry.io/api/embed/error-page/",
      ];
      init({
        app: nuxtApp.vueApp,
        dsn: publicConfig.SENTRY_DSN,
        release: publicConfig.SENTRY_RELEASE,
        environment: publicConfig.SENTRY_ENVIRONMENT,
        enableLogs: true,
        integrations: [
          feedbackIntegration({
            autoInject: false,
            // Additional SDK configuration goes in here, for example:
            colorScheme: "system",
            isNameRequired: true,
            enableScreenshot: true,
            useSentryUser: {
              email: "username",
              name: "fullName",
            },
          }),
          browserTracingIntegration({
            // nuxtApp.$router's inferred type varies by workspace (unknown/Router/RouterClassic), so go through unknown
            router: nuxtApp.$router as unknown as NonNullable<
              Parameters<typeof browserTracingIntegration>[0]
            >["router"],
            routeLabel: "path",
          }),
          replayIntegration({
            networkDetailAllowUrls,
            networkRequestHeaders: ["Nexcor-Plant"],
            networkCaptureBodies: true,
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
          }),
          httpClientIntegration({}),
          reportingObserverIntegration(),
          vueIntegration({
            tracingOptions: {
              trackComponents: true,
              timeout: 1000,
              hooks: ["activate", "create", "destroy", "mount", "update"],
            },
          }),
        ],
        beforeSend(event) {
          // Check if it is an exception, and if so, show the report dialog
          if (event.exception && event.event_id) {
            showReportDialog({ eventId: event.event_id });
          }
          return event;
        },
        tracePropagationTargets: publicConfig.SENTRY_TRACE_PROPAGATION_TARGET
          ?.length
          ? [publicConfig.SENTRY_TRACE_PROPAGATION_TARGET]
          : undefined,
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 0.5,

        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        replaysSessionSampleRate: 0.5,
        replaysOnErrorSampleRate: 1,
      });

      if (import.meta.client) {
        addBreadcrumb({
          category: "version",
          level: "log",
          message: window.ckVersion(),
        });
      }

      provide = {
        sentrySetContext: setContext,
        sentrySetUser: setUser,
        sentrySetTag: setTag,
        sentryAddBreadcrumb: addBreadcrumb,
        sentryLogger: logger,
        sentryCaptureException: captureException,
      };
    }

    return {
      provide,
    };
  },
});
